import { useEffect, useState } from 'react';
import { FiAlertCircle, FiCheckCircle, FiPackage, FiPlus, FiSearch, FiX } from 'react-icons/fi';
import { Link, useSearchParams } from 'react-router-dom';
import { ItemCard, ItemCardSkeleton } from '../components/ItemCard';
import { Header, Sidebar } from '../components/Layout';
import { SidebarProvider } from '../components/SidebarContext';
import { StatsCards } from '../components/StatsCards';
import { useCategories, useFoundItems } from '../hooks/useFoundItems';

const statusOptions = [
	{ value: 'disponivel', label: 'A procurar', icon: FiAlertCircle, color: 'text-amber-600' },
	{ value: 'devolvido', label: 'Devolvido', icon: FiCheckCircle, color: 'text-green-600' },
] as const;

export function Dashboard() {
	const [searchParams, setSearchParams] = useSearchParams();
	const [search, setSearch] = useState(searchParams.get('search') ?? '');
	const [debouncedSearch, setDebouncedSearch] = useState(search);

	const status = (searchParams.get('status') as 'disponivel' | 'devolvido') ?? 'disponivel';
	const category = searchParams.get('category') ?? undefined;
	const page = Number(searchParams.get('page') ?? 1);
	const limit = 12;

	const {
		data: itemsData,
		isLoading,
		isFetching,
		isError,
		error,
		refetch,
	} = useFoundItems({
		status,
		category,
		search: debouncedSearch || undefined,
		page,
		limit,
	});

	const { data: categories } = useCategories();

	const totalItems = itemsData?.meta?.total ?? 0;
	const totalPages = Math.ceil(totalItems / limit);

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearch(search);
		}, 300);
		return () => clearTimeout(timer);
	}, [search]);

	function handleSearchChange(value: string) {
		setSearch(value);
		const params = new URLSearchParams(searchParams);
		if (value.trim()) params.set('search', value.trim());
		else params.delete('search');
		params.delete('page');
		setSearchParams(params);
	}

	function handleStatusChange(newStatus: 'disponivel' | 'devolvido') {
		const params = new URLSearchParams(searchParams);
		params.set('status', newStatus);
		params.delete('page');
		setSearchParams(params);
	}

	function handleCategoryChange(newCategory: string | undefined) {
		const params = new URLSearchParams(searchParams);
		if (newCategory) params.set('category', newCategory);
		else params.delete('category');
		params.delete('page');
		setSearchParams(params);
	}

	function handlePageChange(newPage: number) {
		const params = new URLSearchParams(searchParams);
		params.set('page', String(newPage));
		setSearchParams(params);
	}

	function clearFilters() {
		setSearch('');
		const params = new URLSearchParams();
		params.set('status', 'disponivel');
		setSearchParams(params);
	}

	const hasFilters = search || category || status !== 'disponivel';

	return (
		<SidebarProvider>
			<Sidebar />
			<div className="lg:ml-64 min-h-screen bg-gray-50">
				<Header />
				<main className="max-w-7xl mx-auto px-4 py-6">
					<div className="mb-6 flex items-start justify-between">
						<div>
							<h1 className="text-2xl font-bold text-gray-900">Itens Encontrados</h1>
							<p className="text-gray-500 mt-1">
								{totalItems} {totalItems === 1 ? 'item' : 'itens'} encontrado
								{totalItems !== 1 ? 's' : ''}
								{isFetching && !isLoading && (
									<span className="text-xs text-gray-400 ml-2">atualizando…</span>
								)}
							</p>
						</div>
						<Link
							to="/items/new"
							className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700"
						>
							<FiPlus size={18} /> Novo item
						</Link>
					</div>

					<StatsCards />

					<div className="flex flex-col sm:flex-row gap-4 mb-6">
						<div className="flex-1 relative">
							<FiSearch
								className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
								size={18}
							/>
							<input
								type="search"
								placeholder="Buscar por título ou descrição..."
								value={search}
								onChange={(e) => handleSearchChange(e.target.value)}
								className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
							/>
						</div>

						<div className="flex items-center gap-2">
							<select
								value={status}
								onChange={(e) => handleStatusChange(e.target.value as 'disponivel' | 'devolvido')}
								className="px-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
							>
								{statusOptions.map((s) => (
									<option key={s.value} value={s.value}>
										{s.label}
									</option>
								))}
							</select>

							<select
								value={category ?? ''}
								onChange={(e) => handleCategoryChange(e.target.value || undefined)}
								className="px-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
							>
								<option value="">Todas as categorias</option>
								{categories?.map((c) => (
									<option key={c.id} value={c.slug}>
										{c.name}
									</option>
								))}
							</select>

							{hasFilters && (
								<div className="flex items-center gap-2 flex-wrap">
									{search && (
										<button
											onClick={() => handleSearchChange('')}
											className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-amber-50 text-amber-700 rounded-full hover:bg-amber-100"
										>
											Busca: "{search}" <FiX size={14} />
										</button>
									)}
									{category && (
										<button
											onClick={() => handleCategoryChange(undefined)}
											className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-amber-50 text-amber-700 rounded-full hover:bg-amber-100"
										>
											Categoria: {categories?.find((c) => c.slug === category)?.name}{' '}
											<FiX size={14} />
										</button>
									)}
									{status !== 'disponivel' && (
										<button
											onClick={() => handleStatusChange('disponivel')}
											className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-amber-50 text-amber-700 rounded-full hover:bg-amber-100"
										>
											{statusOptions.find((s) => s.value === status)?.label} <FiX size={14} />
										</button>
									)}
								</div>
							)}
						</div>
					</div>

					{isLoading ? (
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
							{[...Array(8)].map((_, i) => (
								<ItemCardSkeleton key={i} />
							))}
						</div>
					) : isError ? (
						<div className="text-center py-12">
							<FiAlertCircle size={48} className="text-gray-300 mx-auto mb-4" />
							<p className="text-gray-600">Erro ao carregar itens</p>
							{error instanceof Error && (
								<p className="text-sm text-gray-400 mt-1">{error.message}</p>
							)}
							<button onClick={() => refetch()} className="mt-4 text-primary-600 hover:underline">
								Tentar novamente
							</button>
						</div>
					) : itemsData?.data.length === 0 ? (
						<div className="text-center py-12">
							<FiPackage size={48} className="text-gray-300 mx-auto mb-4" />
							<h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum item encontrado</h3>
							<p className="text-gray-500 mb-4">
								{search
									? `Nenhum resultado para "${search}"`
									: 'Não há itens com os filtros atuais'}
							</p>
							{hasFilters && (
								<button onClick={clearFilters} className="text-primary-600 hover:underline">
									Limpar filtros
								</button>
							)}
						</div>
					) : (
						<>
							<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
								{itemsData!.data.map((item) => (
									<ItemCard key={item.id} item={item} />
								))}
							</div>

							{totalPages > 1 && (
								<div className="mt-8 flex items-center justify-center gap-2">
									<button
										onClick={() => handlePageChange(page - 1)}
										disabled={page === 1}
										className="px-4 py-2 text-sm border border-gray-300 rounded-lg bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
									>
										Anterior
									</button>

									<span className="px-4 py-2 text-sm text-gray-600">
										Página {page} de {totalPages}
									</span>

									<button
										onClick={() => handlePageChange(page + 1)}
										disabled={page === totalPages}
										className="px-4 py-2 text-sm border border-gray-300 rounded-lg bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
									>
										Próxima
									</button>
								</div>
							)}
						</>
					)}
				</main>
			</div>
		</SidebarProvider>
	);
}
